import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { X, ShieldCheck, Lock, FileText, Server, Eye, RefreshCw, KeyRound, Globe } from 'lucide-react';

interface PrivacyPolicyModalProps {
  onClose: () => void;
}

export const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ onClose }) => {
  const { t } = useLanguage();

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '780px', padding: '32px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Lock size={24} color="var(--accent-primary)" />
            <div>
              <h3 style={{ fontSize: '20px', fontWeight: 800 }}>
                {t('Política de Privacidad y Términos de Protección de Datos', 'Comprehensive Privacy Policy & Data Terms')}
              </h3>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                {t('Última actualización: Agosto 2026 | Versión 2.1 Enterprise', 'Last updated: August 2026 | Version 2.1 Enterprise')}
              </div>
            </div>
          </div>
          <button className="btn btn-secondary" style={{ padding: '6px' }} onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Regulatory Banner */}
        <div style={{
          background: 'rgba(16, 185, 129, 0.12)',
          padding: '12px 16px',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--accent-success)',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <ShieldCheck size={20} color="var(--accent-success)" />
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--accent-success)' }}>
            {t(
              'Cumplimiento Marco RGPD (UE 2016/679), Ley 1581 de 2012 (LATAM/Colombia), Decreto 1377 y Estándares W3C Open Badges 3.0',
              'Compliant with GDPR (EU 2016/679), Law 1581 of 2012 (LATAM), Decree 1377 & W3C Open Badges 3.0 Standard'
            )}
          </span>
        </div>

        {/* Policy Body */}
        <div style={{
          fontSize: '13px',
          color: 'var(--text-secondary)',
          lineHeight: 1.6,
          maxHeight: '55vh',
          overflowY: 'auto',
          paddingRight: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          {/* Section 1 */}
          <div style={{ background: 'var(--bg-card)', padding: '16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
            <h4 style={{ color: 'var(--text-primary)', fontSize: '15px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Eye size={16} color="var(--accent-primary)" />
              1. {t('Responsable del Tratamiento y Datos Recopilados', 'Data Controller & Categories of Data Collected')}
            </h4>
            <p>
              {t(
                'e-learning by akretrix (Akretrix Organization) actúa como Encargado y/o Responsable del Tratamiento de Datos. Recopilamos datos de identificación (nombre, correo electrónico, teléfono), datos de vinculación corporativa (empresa, NIT, rol), respuestas de evaluaciones y registros de auditoría de streaming de video.',
                'e-learning by akretrix acts as Data Controller and Processor. We collect identity data (full name, corporate email, phone number), employment data (company name, tax ID, role), assessment responses, and video module audit logs.'
              )}
            </p>
          </div>

          {/* Section 2 */}
          <div style={{ background: 'var(--bg-card)', padding: '16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
            <h4 style={{ color: 'var(--text-primary)', fontSize: '15px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Server size={16} color="var(--accent-primary)" />
              2. {t('Finalidad del Tratamiento y Base Legal', 'Purpose of Processing & Legal Basis')}
            </h4>
            <p>
              {t(
                'Los datos son procesados bajo las bases legales de Cumplimiento Legal (capacitaciones obligatorias SG-SST, SARLAFT, Ciberseguridad, Código de Conducta), Ejecución Contractual y Legítimo Interés del Empleador para fines de auditoría laboral y regulatoria.',
                'Data processing is carried out under the legal bases of Statutory Legal Obligation (mandatory training for SG-SST, SARLAFT/AML, GDPR, Cybersecurity, Code of Conduct), Contractual Performance, and Employer Legitimate Interest for corporate compliance audits.'
              )}
            </p>
          </div>

          {/* Section 3 */}
          <div style={{ background: 'var(--bg-card)', padding: '16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
            <h4 style={{ color: 'var(--text-primary)', fontSize: '15px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <KeyRound size={16} color="var(--accent-primary)" />
              3. {t('Insignias Verificables Open Badges 3.0 y Registro Criptográfico', 'Verifiable Credentials & Cryptographic Ledger')}
            </h4>
            <p>
              {t(
                'Al aprobar una evaluación final con un puntaje >= 80%, el sistema genera una Credencial Verificable Open Badges 3.0 (W3C JSON-LD). El usuario autoriza la publicación de su firma criptográfica SHA-256 en la URL pública de verificación (/verify/{badge_id}), la cual cuenta con una vigencia técnica estricta de 365 días.',
                'Upon passing final assessments with a score >= 80%, an Open Badges 3.0 Verifiable Credential (W3C JSON-LD) is created. You explicitly authorize the publication of your SHA-256 cryptographic signature at the public verification URL (/verify/{badge_id}) with a strict 365-day annual vigency.'
              )}
            </p>
          </div>

          {/* Section 4 */}
          <div style={{ background: 'var(--bg-card)', padding: '16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
            <h4 style={{ color: 'var(--text-primary)', fontSize: '15px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <RefreshCw size={16} color="var(--accent-primary)" />
              4. {t('Retención de Datos y Auditorías Corporativas', 'Data Retention & Corporate Audit Logs')}
            </h4>
            <p>
              {t(
                'Los registros de capacitación y certificados emitidos se conservan durante un período mínimo de 5 años para satisfacer requerimientos de auditorías de autoridades laborales (Ministerio del Trabajo, Superintendencias) y estándares internacionales ISO 27001.',
                'Compliance logs and issued certificates are retained for a statutory minimum period of 5 years to fulfill labor authority audit requirements (Ministry of Labor, Superintendencies) and ISO 27001 compliance standards.'
              )}
            </p>
          </div>

          {/* Section 5 */}
          <div style={{ background: 'var(--bg-card)', padding: '16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
            <h4 style={{ color: 'var(--text-primary)', fontSize: '15px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Globe size={16} color="var(--accent-primary)" />
              5. {t('Derechos ARCO, RGPD y Transferencia Internacional', 'ARCO / GDPR Rights & Infrastructure Security')}
            </h4>
            <p>
              {t(
                'Toda la información reside en servidores cifrados en reposo (AES-256) y en tránsito (TLS 1.3) alojados en AWS. Los titulares pueden ejercer sus derechos de Acceso, Rectificación, Cancelación y Oposición (ARCO) o Portabilidad RGPD contactando a nuestro Oficial de Protección de Datos en privacy@akretrix.com.',
                'Data is stored in Amazon Web Services (AWS) using AES-256 encryption at rest and TLS 1.3 in transit. Data subjects can exercise their Access, Rectification, Erasure, and Portability rights by contacting our Data Protection Officer at privacy@akretrix.com.'
              )}
            </p>
          </div>
        </div>

        {/* Action Button */}
        <button className="btn btn-primary" style={{ width: '100%', marginTop: '20px', padding: '12px' }} onClick={onClose}>
          <FileText size={16} />
          {t('Aceptar Política de Privacidad y Términos', 'Accept Comprehensive Privacy Policy & Terms')}
        </button>
      </div>
    </div>
  );
};
